import { Skeleton, Stack, Typography } from "@mui/material";
import AcxInputLabel from "components/UI/AcxInputLabel";
import AcxSortableList from "components/UI/AcxSortableList";
import type { AcxSortableItem } from "components/UI/AcxSortableList";
import { observer } from "mobx-react";
import React, { useEffect, useMemo, useState } from "react";
import { resolveReorderAudience } from "../_DataEndpointConfig/Reorder";
import type { ReorderOption } from "../_DataEndpointConfig/Types";
import ChartCardStore from "../ChartCard/ChartCardStore";

type Props = {
    chartStore: ChartCardStore;
    localOrder: string[];
    setLocalOrder: React.Dispatch<React.SetStateAction<string[]>>;
};

/**
 * Generic, config-driven section that lets the permitted audience drag-reorder the values of a chart's
 * x-axis order rule. The chart's `config.reorder` supplies the section label, audience, and resolves the
 * value ids to display names; ordering itself lives in the drawer's `localOrder` so it can
 * preview/revert/save. Whether this renders at all is decided by `canReorderChart` in the drawer.
 */
export const ReorderConfigurationSection: React.FC<Props> = observer(
    ({ chartStore, localOrder, setLocalOrder }) => {
        const reorderConfig = chartStore.config.reorder;
        const [labelsById, setLabelsById] = useState<Map<string, string>>(
            new Map(),
        );
        const [isLoading, setIsLoading] = useState(true);

        useEffect(() => {
            if (!reorderConfig) return;
            let disposed = false;
            setIsLoading(true);
            void reorderConfig
                .loadOptions({ dataEndpoint: chartStore.dataEndpoint })
                .then((options: ReorderOption[]) => {
                    if (disposed) return;
                    setLabelsById(
                        new Map(
                            options.map((option) => [option.id, option.name]),
                        ),
                    );
                })
                .finally(() => {
                    if (!disposed) setIsLoading(false);
                });
            return () => {
                disposed = true;
            };
        }, [reorderConfig, chartStore.dataEndpoint]);

        const items = useMemo<AcxSortableItem[]>(
            // Keep every id in localOrder so reordering never drops a value (the backend requires a
            // permutation). Labels fall back to the id on the off chance one can't be resolved.
            () =>
                localOrder.map((id) => ({
                    id,
                    label: labelsById.get(id) ?? id,
                })),
            [localOrder, labelsById],
        );

        if (!reorderConfig) return null;
        // Once loaded, render nothing when there are no resolvable values (e.g. a missing group). While
        // loading we still show a skeleton so the section doesn't pop in unannounced.
        if (!isLoading && labelsById.size === 0) return null;

        // We already know how many rows to expect from localOrder (the order-rule values), so the skeleton
        // matches the eventual list height. Fall back to a small placeholder count if it's somehow empty.
        const skeletonCount = localOrder.length || 3;

        // Only internal-audience charts get the "you see this because you're an Authenticx user" note;
        // the persistence caveat applies to everyone who can reorder.
        const isInternalOnly = resolveReorderAudience(chartStore) === "internal";
        const helperText = isInternalOnly
            ? "Visible to you as an Authenticx user. Reordering here is saved for all users of this organization once applied."
            : "Reordering here is saved for all users of this organization once applied.";

        return (
            <Stack direction="column" rowGap={0.75} sx={{ minWidth: 0 }}>
                <AcxInputLabel
                    labelText={reorderConfig.label}
                    sx={(theme) => ({
                        fontFamily: theme.typography.fontFamily,
                        color: theme.palette.text.primary,
                        fontWeight: theme.typography.fontWeightRegular,
                        fontSize: "12px",
                        lineHeight: "16px",
                    })}
                />
                {isLoading ? (
                    <Stack direction="column" rowGap={1}>
                        {Array.from({ length: skeletonCount }).map(
                            (_value, index) => (
                                <Skeleton
                                    key={index}
                                    variant="rounded"
                                    height={38}
                                />
                            ),
                        )}
                    </Stack>
                ) : (
                    <AcxSortableList
                        items={items}
                        onReorder={setLocalOrder}
                        idPrefix={`${chartStore.id}-reorder`}
                    />
                )}
                <Typography
                    variant="caption"
                    sx={(theme) => ({
                        display: "block",
                        color: theme.palette.gray[600],
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                    })}
                >
                    {helperText}
                </Typography>
            </Stack>
        );
    },
);

export default ReorderConfigurationSection;
